import { RewardProfileCard } from "@/components/client/RewardProfile";
import { Star } from "@/lib/icons";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { Sparkles, ShieldCheck, MessageCircleHeart, BookOpen, Bell, RefreshCcw } from "@/lib/icons";

const FEEDBACK_OPTIONS = [{ v: "motivating", label: "Motivating" }, { v: "neutral", label: "Neutral" }, { v: "deterring", label: "Not for me" }];

const REVIEW_PILLS = {
  pending: { text: "Awaiting your coach's look", cls: "bg-stone-100 text-stone-600" },
  approved: { text: "Reviewed by your coach ✓", cls: "bg-emerald-50 text-emerald-800" },
  edited: { text: "Adjusted by your coach", cls: "bg-emerald-50 text-emerald-800" },
};

const GENERAL_PILL = { text: "AI suggestion · not clinically reviewed", cls: "bg-amber-50 text-amber-800" };

function ReviewPill({ r }) {
  const p = REVIEW_PILLS[r.review_status] || (r.source === "ai" && r.lane === "general" ? GENERAL_PILL : null);
  if (!p) return null;
  return <span data-testid={`reward-review-${r.id}`} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.cls}`}><ShieldCheck className="w-3 h-3" /> {p.text}</span>;
}

function WithdrawnCard({ r }) {
  return (
    <div className="warm-card p-5 opacity-80 border-dashed" data-testid={`reward-card-${r.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">For: {r.habit_title}</div>
      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-stone-100 text-stone-600" data-testid={`reward-withdrawn-${r.id}`}>
        <RefreshCcw className="w-3 h-3" /> Swapped out by your coach
      </div>
      <p className="text-sm text-stone-600 mt-2">Your coach set this suggestion aside{r.withdrawn_note ? `: "${r.withdrawn_note}"` : "."} Tap "Get a reward" for a fresh one{r.claimed ? " — the points you earned stay yours" : ""}.</p>
    </div>
  );
}

function RewardCard({ r, onClaim, onFeedback, onFavorite, onDelete }) {
  if (r.withdrawn) return <WithdrawnCard r={r} />;
  return (
    <div className="warm-card p-5" data-testid={`reward-card-${r.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">For: {r.habit_title}</div>
        <ReviewPill r={r} />
      </div>
      {r.resilience_level > 0 && (
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`reward-resilience-${r.id}`}>
          <ShieldCheck className="w-3 h-3" /> Earned during {r.resilience_label}
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mt-1">
        <h4 className="font-display font-bold text-stone-900">{r.title}</h4>
        <button onClick={() => onFavorite(r.id)} data-testid={`reward-fav-${r.id}`} title={r.favorite ? "Starred — may come back on a future win" : "Star it so it can come back on a future win"} aria-pressed={!!r.favorite}
          className={`shrink-0 p-1 rounded-full transition-colors ${r.favorite ? "text-amber-600" : "text-stone-300 hover:text-amber-600"}`}><Star className={`w-4 h-4 ${r.favorite ? "fill-amber-400" : ""}`} /></button>
      </div>
      {(r.source === "favorite" || r.source === "clinician_idea") && <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mt-1" data-testid={`reward-source-${r.id}`}>{r.source === "favorite" ? "One of your favourites, back again" : "Suggested by your coach"}</div>}
      <p className="text-sm text-stone-600 mt-2">{r.description}</p>
      {r.action && <p className="text-xs text-amber-800 mt-2 font-medium italic">→ {r.action}</p>}
      <div className="flex flex-wrap gap-2 mt-4">
        {!r.claimed ? (
          <>
            <button data-testid={`claim-${r.id}`} onClick={() => onClaim(r.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700">Claim it</button>
            {onDelete && <button data-testid={`reward-delete-${r.id}`} onClick={() => onDelete(r.id)} title="Remove this reward" className="text-xs font-semibold px-3 py-1.5 rounded-full border border-stone-300 text-stone-600 hover:border-rose-400 hover:text-rose-700 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
          </>
        ) : (
          <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Claimed ✓</span>
        )}
        {!r.feedback && r.claimed && (
          <div className="flex gap-1">
            {FEEDBACK_OPTIONS.map((f) => (
              <button key={f.v} data-testid={`feedback-${f.v}-${r.id}`} onClick={() => onFeedback(r.id, f.v)} className="text-xs px-2.5 py-1 rounded-full border border-stone-300 hover:bg-stone-100">{f.label}</button>
            ))}
          </div>
        )}
        {r.feedback && <span className="text-xs px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 capitalize">You said: {r.feedback}</span>}
      </div>
    </div>
  );
}

export function AiStatusNote({ status }) {
  if (!status || (!status.reviewed_by_clinician && !status.pause && !status.ai_off)) return null;
  return (
    <div className="mb-4 space-y-2" data-testid="ai-status-note">
      {status.ai_off && (
        <p className="text-xs text-stone-500 inline-flex items-start gap-1.5" data-testid="ai-off-note"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-stone-500 shrink-0" />
          AI is off for your account — rewards come from classic picks, your favourites and coach ideas. Change this any time in <Link to="/security" className="underline">Security &amp; Privacy</Link>.</p>
      )}
      {status.reviewed_by_clinician && !status.ai_off && (
        <p className="text-xs text-stone-500 inline-flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-700 shrink-0" />
          AI suggestions for you are generated from anonymous signals only and looked over by your coach{status.pending_review > 0 ? ` (${status.pending_review} waiting)` : ""}.</p>
      )}
      {status.pause && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="ai-paused-notice">{status.pause.message}</div>
      )}
    </div>
  );
}

export function RewardsSection({ rewards, onClaim, onFeedback, onFavorite, onDelete, aiStatus }) {
  return (
    <div className="pt-4">
      <h2 className="font-display text-2xl font-bold text-stone-900 mb-4">Your rewards</h2>
      <AiStatusNote status={aiStatus} />
      <RewardProfileCard />
      {rewards.length === 0 && (
        <div className="warm-card p-8 text-center text-stone-500">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-amber-600" />
          Complete a habit or tap "Get a reward" on any habit to receive your first personalized reward.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {rewards.map((r) => <RewardCard key={r.id} r={r} onClaim={onClaim} onFeedback={onFeedback} onFavorite={onFavorite} onDelete={onDelete} />)}
      </div>
    </div>
  );
}

export function ClinicianNote({ rec, habits, onSetReminder }) {
  if (!rec) return null;
  const canSetReminder = rec.reminder_time && habits.some((h) => h.id === rec.reminder_habit_id);
  return (
    <div className="warm-card p-4 min-w-[220px]" data-testid="clinician-note">
      <div className="flex items-center gap-2 text-emerald-800 text-xs font-bold uppercase tracking-widest mb-1">
        <MessageCircleHeart className="w-4 h-4" /> From {rec.clinician_name}
      </div>
      <div className="text-sm text-stone-700">"{rec.message}"</div>
      {rec.article_id && (
        <Link to={`/learn?article=${rec.article_id}`} data-testid="clinician-note-article-link" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-amber-900">
          <BookOpen className="w-3.5 h-3.5" /> Read: {rec.article_title}
        </Link>
      )}
      {canSetReminder && (
        <button data-testid="clinician-note-set-reminder" onClick={() => onSetReminder({ habit_id: rec.reminder_habit_id, time: rec.reminder_time })}
          className="mt-2 ml-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-amber-700">
          <Bell className="w-3.5 h-3.5" /> Set reminder: {rec.reminder_habit_title} at {rec.reminder_time}
        </button>
      )}
    </div>
  );
}
